/**
 * Shared zod schemas + shapes used by multiple contracts. Keep this small —
 * domain shapes live in their own contract files.
 */

import { z } from "zod";

export const idNumericParams = z.object({ id: z.coerce.number().int() });
export const slugParams = z.object({ slug: z.string().min(1) });

/**
 * Standard pagination query. Compose into a contract's query schema via
 * `paginationQuery.extend({ ...other filters })`. Both fields are optional
 * — routes apply defaults (page=1, page_size=50).
 *
 * page_size is capped at 200 to keep an unbounded `?page_size=999999` from
 * being a back-door to fetch everything.
 */
export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).optional(),
  page_size: z.coerce.number().int().min(1).max(200).optional(),
});

export type PaginationQuery = z.infer<typeof paginationQuery>;

export const DEFAULT_PAGE_SIZE = 50;

export interface PaginatedResult<T> {
  rows: T[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

/**
 * Zod schema generator for a paginated response envelope. Pair with
 * `paginationQuery` on the query side. Same envelope for every paginated
 * endpoint — clients can write one `<TablePager>` against it.
 */
export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    rows: z.array(item),
    page: z.number().int(),
    page_size: z.number().int(),
    total: z.number().int(),
    total_pages: z.number().int(),
    has_next: z.boolean(),
    has_prev: z.boolean(),
  });
}

/**
 * Helper for route handlers: resolves defaults, computes derived counts.
 * Pass the rows for the requested page and the total matching count.
 */
export function buildPaginated<T>(
  rows: T[],
  total: number,
  query: PaginationQuery | undefined,
): PaginatedResult<T> {
  const page = query?.page ?? 1;
  const page_size = query?.page_size ?? DEFAULT_PAGE_SIZE;
  const total_pages = total === 0 ? 0 : Math.ceil(total / page_size);
  return {
    rows,
    page,
    page_size,
    total,
    total_pages,
    has_next: page < total_pages,
    has_prev: page > 1,
  };
}

/**
 * Helper for DB layers: compute SQL LIMIT/OFFSET from pagination query.
 * Always applies the cap that matches paginationQuery's max.
 */
export function pageOffset(query: PaginationQuery | undefined): {
  limit: number;
  offset: number;
  page: number;
  page_size: number;
} {
  const page = Math.max(1, query?.page ?? 1);
  const page_size = Math.min(200, Math.max(1, query?.page_size ?? DEFAULT_PAGE_SIZE));
  return { limit: page_size, offset: (page - 1) * page_size, page, page_size };
}

// ── Keyset (cursor) pagination — for DEEP append-only feeds ──────────────────
// The contract half of `paginateKeyset` (packages/core/src/pagination.ts): a
// "load more" feed instead of "page N of M". No total — feeds are unbounded.

/** Decoded cursor shape (mirrors core `KeysetCursor`). */
export interface CursorValue {
  v: string | number;
  t?: string | number;
}

/**
 * Standard keyset query: an opaque `cursor` (omit for page 1) + `limit`. Compose
 * via `keysetQuery.extend({ …filters })`. The cursor is produced by the previous
 * response's `next_cursor` and passed back verbatim — clients treat it opaque.
 */
export const keysetQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type KeysetQuery = z.infer<typeof keysetQuery>;

export const DEFAULT_FEED_LIMIT = 30;

/** Response envelope for a keyset feed: a page of rows + the next cursor. */
export function keysetSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    rows: z.array(item),
    next_cursor: z.string().nullable(),
    has_more: z.boolean(),
  });
}

export interface KeysetResult<T> {
  rows: T[];
  next_cursor: string | null;
  has_more: boolean;
}

/**
 * Encode a decoded cursor to the opaque wire string (base64 JSON). Cursor values
 * are numbers / ISO strings (ASCII), so `btoa` — available in both the Node
 * route runtime and the browser — is safe.
 */
export function encodeCursor(c: CursorValue | null | undefined): string | null {
  if (c == null) return null;
  return btoa(JSON.stringify(c));
}

/** Decode an opaque wire cursor back to `{ v, t? }`; null/invalid → null (page 1). */
export function decodeCursor(s: string | null | undefined): CursorValue | null {
  if (!s) return null;
  try {
    const parsed = JSON.parse(atob(s)) as CursorValue;
    return parsed && "v" in parsed ? parsed : null;
  } catch {
    return null;
  }
}

/** Route helper: turn a core `KeysetPage` into the wire envelope. */
export function buildKeyset<T>(page: {
  rows: T[];
  nextCursor: CursorValue | null;
  hasMore: boolean;
}): KeysetResult<T> {
  return {
    rows: page.rows,
    next_cursor: encodeCursor(page.nextCursor),
    has_more: page.hasMore,
  };
}

/**
 * A single verification run. Returned by both:
 *   - tasks.verifications (`GET /projects/api/tasks/:id/verifications`)
 *   - autoVerify.run     (`GET /projects/api/auto-verify/runs/:id`)
 *
 * Lives here so the two endpoints share one shape (the prior contracts had
 * two near-identical interfaces — `VerificationRun` in tasks.ts and
 * `AutoVerifyRun` in auto-verify.ts — that drifted in their `decision` field
 * typing).
 */
export interface VerificationRun {
  id: number;
  task_id: number;
  status: "running" | "completed" | "error" | "timeout" | "superseded";
  started_at: string;
  finished_at: string | null;
  decision:
    | "allow"
    | "return"
    | "hold"
    | "block"
    | "advisory"
    | "indeterminate"
    | null;
  decision_note: string | null;
  output: string | null;
  agent_id: string | null;
  exit_code: number | null;
}
